import { useState, useEffect, useCallback } from 'react';
import { db } from '../lib/firebase';
import { collection, onSnapshot, doc, deleteDoc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { FolderArchive, X, Search, Copy, Trash2, ArrowUpRight, RefreshCw, Plus } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import type { ClientMeta, GlobalConfig, PricingRow, SpecItem, KitchenUnit, ProjectMeta } from '../types';
import { getUserInitials, getUserAvatarColor } from '../utils/avatarUtils';

export interface SavedProposalDoc {
  id: string;
  proposalId: string;
  designerId: string;
  designerName: string;
  clientName: string;
  status: 'draft' | 'quoted' | 'won' | 'lost';
  totalAmount: number;
  elapsedSeconds: number;
  client: ClientMeta;
  config: GlobalConfig;
  pricingRows: PricingRow[];
  specItems: SpecItem[];
  projectImages: string[];
  createdAt: string;
  updatedAt: string;
  // Bulk/project proposal fields — absent (or 'retail') on every existing single-kitchen proposal.
  proposalType?: 'retail' | 'bulk';
  projectMeta?: ProjectMeta;
  units?: KitchenUnit[];
}

export default function ProposalsModal() {
  const {
    currentUser,
    userName,
    userRole,
    isProposalsModalOpen,
    setIsProposalsModalOpen,
    loadProposalFromCloud,
    cloneProposalFromCloud,
    handleNewProposal,
    setMode,
  } = useAppContext();

  const [proposals, setProposals] = useState<SavedProposalDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'quoted' | 'won' | 'lost'>('all');

  const fetchProposals = useCallback(async () => {
    if (!db || !currentUser) return;
    setLoading(true);
    try {
      const colRef = collection(db, 'proposals');
      const isAdminOrManager = userRole === 'admin';

      const q = isAdminOrManager
        ? colRef
        : query(colRef, where('designerId', '==', currentUser.uid));

      const snapshot = await getDocs(q);
      const list: SavedProposalDoc[] = [];
      snapshot.forEach(docSnap => {
        const data = docSnap.data() as SavedProposalDoc;
        list.push({
          ...data,
          id: docSnap.id,
        });
      });
      setProposals(list);
    } catch (err) {
      console.error("Manual fetch proposals failed:", err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, userRole]);

  useEffect(() => {
    if (!db || !isProposalsModalOpen || !currentUser) return;

    setLoading(true);
    const colRef = collection(db, 'proposals');
    const isAdminOrManager = userRole === 'admin';

    // Server-side query filtering: Admin/Manager sees all, Designers query by designerId
    const q = isAdminOrManager
      ? colRef
      : query(colRef, where('designerId', '==', currentUser.uid));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list: SavedProposalDoc[] = [];
      snapshot.forEach(docSnap => {
        const data = docSnap.data() as SavedProposalDoc;
        list.push({
          ...data,
          id: docSnap.id,
        });
      });
      setProposals(list);
      setLoading(false);
    }, (err) => {
      console.error("Failed to load proposals list via snapshot:", err);
      // Fallback: Try fetching directly if snapshot query hits an index or role delay
      fetchProposals();
    });

    return () => unsubscribe();
  }, [isProposalsModalOpen, userRole, currentUser?.uid, fetchProposals]);

  if (!isProposalsModalOpen) return null;

  // Filter proposals by user access & search
  const visibleProposals = proposals.filter(p => {
    const isAdminOrManager = userRole === 'admin';

    if (!isAdminOrManager) {
      // Designer can see proposals created by their UID, email, or designer name
      const isOwner =
        (p.designerId && p.designerId === currentUser?.uid) ||
        (p.designerName && p.designerName.toLowerCase() === currentUser?.email?.toLowerCase()) ||
        (p.designerName && userName && p.designerName.toLowerCase() === userName.toLowerCase()) ||
        (p.client?.designer && userName && p.client.designer.toLowerCase() === userName.toLowerCase());

      if (!isOwner) return false;
    }

    if (statusFilter !== 'all' && p.status !== statusFilter) {
      return false;
    }

    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (p.clientName || p.client?.clientName || '').toLowerCase().includes(q) ||
      (p.designerName || p.client?.designer || '').toLowerCase().includes(q) ||
      (p.proposalId || '').toLowerCase().includes(q)
    );
  });

  // Sort by updatedAt desc
  const sortedProposals = [...visibleProposals].sort((a, b) => 
    new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime()
  );

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(val);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleUpdateStatus = async (proposalId: string, newStatus: 'draft' | 'quoted' | 'won' | 'lost') => {
    if (!db) return;
    try {
      await updateDoc(doc(db, 'proposals', proposalId), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
    } catch (err: any) {
      console.error("Failed to update status:", err);
      alert("Durum güncellenirken hata oluştu: " + err.message);
    }
  };

  const handleDeleteProposal = async (proposalId: string, clientName: string, proposalOwnerId?: string) => {
    if (!db) return;
    if (proposalOwnerId && currentUser && proposalOwnerId !== currentUser.uid) {
      alert("Yalnızca kendi oluşturduğunuz teklifleri silebilirsiniz.");
      return;
    }
    if (!window.confirm(`'${clientName || 'İsimsiz Müşteri'}' teklif kaydını arşivden silmek istediğinize emin misiniz?`)) {
      return;
    }
    try {
      await deleteDoc(doc(db, 'proposals', proposalId));
    } catch (err: any) {
      console.error("Failed to delete proposal:", err);
      alert("Teklif silinirken hata oluştu: " + err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-xs no-print animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full h-[85vh] flex flex-col overflow-hidden border border-stone-200">
        
        {/* Header */}
        <div className="bg-stone-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#945f46]/20 text-[#945f46] rounded-xl border border-[#945f46]/30">
              <FolderArchive className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-sm font-extrabold uppercase tracking-wider">Teklif Arşivi & Geçmiş Kayıtlar</h2>
              <p className="text-[10px] text-stone-400 normal-case">Buluta kaydedilmiş tüm tekliflerinizi inceleyin, tekrar açın veya klonlayın.</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={fetchProposals}
              className="p-1.5 bg-stone-800 hover:bg-stone-700 text-stone-300 rounded-lg text-xs font-semibold flex items-center space-x-1 transition-colors"
              title="Teklif Listesini Yenile"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Yenile</span>
            </button>

            <span className="bg-stone-800 text-stone-300 text-[10px] font-bold px-2.5 py-1 rounded-full border border-stone-700">
              {sortedProposals.length} Kayıtlı Teklif
            </span>
            <button 
              type="button"
              onClick={() => setIsProposalsModalOpen(false)}
              className="text-stone-400 hover:text-white p-1.5 rounded-lg hover:bg-stone-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filter & Search Toolbar */}
        <div className="bg-stone-50 border-b border-stone-200 px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
          
          {/* Status Filter Buttons */}
          <div className="bg-stone-200/80 p-1 rounded-xl flex items-center space-x-1 text-xs font-bold w-full sm:w-auto overflow-x-auto">
            <button
              type="button"
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1 rounded-lg transition-all ${statusFilter === 'all' ? 'bg-white text-stone-900 shadow-xs' : 'text-stone-600 hover:text-stone-900'}`}
            >
              Tümü
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('draft')}
              className={`px-3 py-1 rounded-lg transition-all ${statusFilter === 'draft' ? 'bg-white text-stone-900 shadow-xs' : 'text-stone-600 hover:text-stone-900'}`}
            >
              Taslak
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('quoted')}
              className={`px-3 py-1 rounded-lg transition-all ${statusFilter === 'quoted' ? 'bg-white text-stone-900 shadow-xs' : 'text-stone-600 hover:text-stone-900'}`}
            >
              Teklif Verildi
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('won')}
              className={`px-3 py-1 rounded-lg transition-all ${statusFilter === 'won' ? 'bg-white text-emerald-700 shadow-xs' : 'text-stone-600 hover:text-stone-900'}`}
            >
              Onaylandı (Satış)
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('lost')}
              className={`px-3 py-1 rounded-lg transition-all ${statusFilter === 'lost' ? 'bg-white text-red-700 shadow-xs' : 'text-stone-600 hover:text-stone-900'}`}
            >
              İptal / Kaybedildi
            </button>
          </div>

          {/* Search Box */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-stone-400" />
            <input
              type="text"
              placeholder="Müşteri veya Tasarımcı ara..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-1.5 text-xs rounded-xl border border-stone-200 bg-white focus:outline-none focus:border-[#945f46] font-medium"
            />
          </div>

        </div>

        {/* Proposals Table */}
        <div className="flex-1 overflow-y-auto p-6 bg-stone-50/40">
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden shadow-xs">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-stone-50 text-[10px] font-bold text-stone-400 border-b border-stone-200 uppercase tracking-wider">
                  <th className="py-3 px-4">Son Güncelleme</th>
                  <th className="py-3 px-4">Müşteri Adı</th>
                  <th className="py-3 px-4">Tasarımcı</th>
                  <th className="py-3 px-4 text-right">Teklif Tutarı</th>
                  <th className="py-3 px-4 text-center">Durum</th>
                  <th className="py-3 px-4 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 font-medium text-stone-700">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-stone-400 text-xs italic">
                      Teklifler bulut arşivinden yükleniyor...
                    </td>
                  </tr>
                ) : sortedProposals.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-stone-400 text-xs italic space-y-2">
                      <p>Kayıtlı teklif bulunamadı.</p>
                      <p className="text-[11px] text-stone-500 not-italic">Mevcut çalışmanızı sağ üstteki <b>'Kaydet'</b> butonuna basarak bulut arşivine ekleyebilirsiniz.</p>
                    </td>
                  </tr>
                ) : (
                  sortedProposals.map((propDoc) => {
                    const isOwnProposal = currentUser && (
                      propDoc.designerId === currentUser.uid ||
                      propDoc.designerName?.toLowerCase() === currentUser.email?.toLowerCase()
                    );

                    return (
                      <tr key={propDoc.id} className="hover:bg-stone-50/70 transition-colors group">
                        <td className="py-3 px-4 text-stone-500 font-semibold">{formatDate(propDoc.updatedAt || propDoc.createdAt)}</td>
                        <td className="py-3 px-4 font-extrabold text-stone-900">
                          <div className="flex items-center gap-1.5">
                            <span>{propDoc.clientName || propDoc.client?.clientName || 'İsimsiz Müşteri'}</span>
                            {propDoc.proposalType === 'bulk' && (
                              <span className="shrink-0 px-1.5 py-0.5 rounded-full bg-brand-100 text-brand-700 text-[9px] font-extrabold uppercase tracking-wider">
                                Toplu Proje{propDoc.units?.length ? ` · ${propDoc.units.reduce((s, u) => s + u.quantity, 0)} Daire` : ''}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-stone-600 font-semibold">
                          <div className="flex items-center space-x-1.5">
                            <div 
                              className="w-5 h-5 rounded-full text-white font-bold text-[8.5px] flex items-center justify-center shrink-0 uppercase shadow-2xs"
                              style={{ backgroundColor: getUserAvatarColor(propDoc.designerName || propDoc.client?.designer) }}
                            >
                              {getUserInitials(propDoc.designerName || propDoc.client?.designer)}
                            </div>
                            <span className="truncate max-w-[140px]">{propDoc.designerName || propDoc.client?.designer || '-'}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right font-extrabold text-stone-900">
                          {formatCurrency(propDoc.totalAmount || 0)}
                        </td>
                        
                        {/* Status Select */}
                        <td className="py-3 px-4 text-center">
                          <select
                            value={propDoc.status || 'draft'}
                            onChange={(e) => handleUpdateStatus(propDoc.id, e.target.value as any)}
                            className={`px-2 py-1 rounded text-[10px] font-bold uppercase cursor-pointer focus:outline-none border ${
                              propDoc.status === 'won'
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : propDoc.status === 'quoted'
                                ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                : propDoc.status === 'lost'
                                ? 'bg-red-50 text-red-700 border-red-200'
                                : 'bg-stone-100 text-stone-700 border-stone-200'
                            }`}
                          >
                            <option value="draft">Taslak</option>
                            <option value="quoted">Teklif Verildi</option>
                            <option value="won">Onaylandı (Satış)</option>
                            <option value="lost">İptal / Kaybedildi</option>
                          </select>
                        </td>

                        {/* Action Buttons */}
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end space-x-1.5">
                            {/* Open / Load */}
                            <button
                              type="button"
                              onClick={() => {
                                loadProposalFromCloud(propDoc);
                                setIsProposalsModalOpen(false);
                                setMode('edit');
                              }}
                              className="px-2.5 py-1 bg-[#945f46]/10 hover:bg-[#945f46]/20 text-[#945f46] border border-[#945f46]/30 rounded-lg text-[11px] font-bold flex items-center space-x-1 transition-colors"
                              title={isOwnProposal ? "Teklifi Düzenlemek için Aç" : "Teklifi İncele (Salt Okunur)"}
                            >
                              <ArrowUpRight className="w-3.5 h-3.5" />
                              <span>{isOwnProposal ? 'Aç' : 'İncele'}</span>
                            </button>

                            {/* Clone / Revise */}
                            <button
                              type="button"
                              onClick={() => {
                                cloneProposalFromCloud(propDoc);
                                setIsProposalsModalOpen(false);
                                setMode('edit');
                              }}
                              className="p-1.5 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors"
                              title="Klonla (Kendi hesabında revizyon / kopya teklif oluştur)"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>

                            {/* Delete (Owner only, including admins) */}
                            {isOwnProposal ? (
                              <button
                                type="button"
                                onClick={() => handleDeleteProposal(propDoc.id, propDoc.clientName || propDoc.client?.clientName, propDoc.designerId)}
                                className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors w-7 h-7 flex items-center justify-center shrink-0"
                                title="Teklifi Sil"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            ) : (
                              <div className="w-7 h-7 shrink-0" />
                            )}
                          </div>
                        </td>

                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-stone-50 px-6 py-3 border-t border-stone-200 flex items-center justify-between text-xs text-stone-500 font-medium">
          <span>Tüm veriler Firestore bulut arşivinde güvenle saklanır.</span>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => {
                setIsProposalsModalOpen(false);
                if (handleNewProposal()) setMode('edit');
              }}
              className="px-3.5 py-1.5 bg-[#945f46] hover:bg-[#83523c] text-white rounded-lg font-bold transition-colors flex items-center space-x-1"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Yeni Teklif Başlat</span>
            </button>
            <button
              type="button"
              onClick={() => setIsProposalsModalOpen(false)}
              className="px-4 py-1.5 bg-stone-200 hover:bg-stone-300 text-stone-700 rounded-lg font-bold transition-colors"
            >
              Kapat
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
